// apps/admin/src/api/finance.api.ts
//
// Phase 8.2 Wave 1 Task 04 — Finance shared API client. Single Axios +
// zod transport for every list endpoint the downstream Finance pages
// (Topup Reviews / Credits / Payouts / Transactions) consume.
//
// Per CONTEXT.md
//   • Billing tabs (Topup Reviews / Credits / Gateways).
//   • Payouts page (vendor payout state machine + KYC gate).
//   • Transactions page (cross-merchant/vendor ledger + 4 summary cards).
//   • Merchant credit schema versioned.
//   • Topup review branches on verification_status.
//
// Per INTERFACE-STANDARDS.md
//   • Success envelope: { ok: true, data, meta?, request_id? }
//   • Error envelope:   { ok: false, error: { code, message, user_message?, ... } }
//   • Message priority: error.user_message > error.message > message > network_fallback
//
// Transport contract:
//   • The canonical `apiClient` (lib/apiClient.ts) ALREADY unwraps the
//     `{ ok, data }` envelope at its response interceptor and throws
//     `ApiError` on every non-2xx. The interceptor drops `meta`, so
//     each LIST helper here uses a local axios clone (`rawAxios`) to
//     keep both `data` (rows) and `meta` (page/limit/total/summary)
//     accessible to the consumer. The clone shares cookie credentials
//     so JWT pw_token + CSRF still flow.
//   • `unwrapFinanceEnvelope` is the safety net: it normalises both
//     success and error shapes per INTERFACE-STANDARDS priority order.
//     Toast helpers consume the resulting `userMessage` directly — they
//     NEVER touch AxiosError.message or Response.statusText.
//
// Wave 2 (Task 06-10) adds mutation endpoints for topup approve/reject,
// payout approve/reject/mark-transferred, and credit grant/revoke. Each
// mutation goes through the canonical `apiClient` (envelope-unwrap +
// `ApiError` throw) — no `rawAxios` because mutations don't carry the
// list `meta` (page/total/summary) we need to preserve on reads.
//
// vg-binding: PLAN:task-04
// vg-binding: P8.D-84
// vg-binding: P8.D-86
// vg-binding: P8.D-87
// vg-binding: P8.D-204
// vg-binding: P8.D-214
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority

import axios from 'axios';
import { z } from 'zod';

import { API_BASE_URL } from '../lib/apiClient.js';

import {
  ApiError,
  apiClient,
  type ApiErrorEnvelope,
} from '../lib/apiClient.js';
import { NETWORK_FALLBACK_MESSAGE } from '../lib/errorHandler.js';

import {
  FinanceCreditMetaSchema,
  FinanceCreditRowSchema,
  AdminInvoiceListMetaSchema,
  AdminInvoiceRowSchema,
  BulkResendInvoiceEmailRequestSchema,
  BulkResendInvoiceEmailResponseSchema,
  PayoutMetaSchema,
  PayoutRowSchema,
  TopupReviewMetaSchema,
  TopupReviewRowSchema,
  TransactionMetaSchema,
  TransactionRowSchema,
  type AdminInvoiceListFilters,
  type AdminInvoiceListPayload,
  type BulkResendInvoiceEmailRequest,
  type BulkResendInvoiceEmailResponse,
  type FinanceCreditListFilters,
  type FinanceCreditListPayload,
  type PayoutListFilters,
  type PayoutListPayload,
  type TopupReviewListFilters,
  type TopupReviewListPayload,
  type TransactionListFilters,
  type TransactionListPayload,
} from '../types/finance.js';

// ---------------------------------------------------------------------------
// Envelope unwrap helper (INTERFACE-STANDARDS message-priority)
// ---------------------------------------------------------------------------

/**
 * Result of a defensive envelope unwrap. Either the unwrapped payload
 * (data + meta) or a normalised error message ready for toast/banner
 * consumption.
 */
export type UnwrapResult<TData, TMeta = undefined> =
  | { ok: true; data: TData; meta: TMeta; requestId?: string }
  | {
      ok: false;
      code: string;
      userMessage: string;
      fieldErrors?: Record<string, string[]>;
      details?: Record<string, unknown>;
      requestId?: string;
    };

/**
 * Defensive envelope unwrap.
 *
 * Accepts ANY shape returned by the server (canonical `{ok, data}`,
 * legacy `{success, data}`, or an error envelope) and resolves to a
 * tagged-union result. Toast/banner consumers branch on `result.ok` and
 * read `result.userMessage` directly — they MUST NOT fall back to
 * `AxiosError.message`, `Response.statusText`, or HTTP status text
 * (banned by INTERFACE-STANDARDS.frontend.toast_rule).
 *
 * Message priority (per INTERFACE-STANDARDS §message-priority):
 *   1. error.user_message
 *   2. error.message
 *   3. message               (top-level — only for legacy/quirky bodies)
 *   4. NETWORK_FALLBACK_MESSAGE
 *
 * `AxiosError.message` is NEVER consulted — the caller wraps `axios` in
 * `apiClient` which throws `ApiError` carrying the already-resolved copy.
 */
export function unwrapFinanceEnvelope<TData, TMeta = undefined>(
  payload: unknown,
): UnwrapResult<TData, TMeta> {
  // Nothing came back — network-fallback.
  if (payload == null || typeof payload !== 'object') {
    return {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK_MESSAGE,
    };
  }

  const body = payload as Record<string, unknown>;

  // ---- success branches -------------------------------------------------
  // Canonical `{ ok: true, data, meta?, request_id? }`.
  if (body.ok === true && 'data' in body) {
    return {
      ok: true,
      data: body.data as TData,
      meta: (body.meta ?? undefined) as TMeta,
      requestId:
        typeof body.request_id === 'string' ? body.request_id : undefined,
    };
  }
  // Legacy `{ success: true, data, ... }` envelope.
  if (body.success === true && 'data' in body) {
    return {
      ok: true,
      data: body.data as TData,
      meta: (body.meta ?? undefined) as TMeta,
      requestId:
        typeof body.requestId === 'string'
          ? body.requestId
          : typeof body.request_id === 'string'
          ? body.request_id
          : undefined,
    };
  }

  // ---- error branches ---------------------------------------------------
  // Canonical `{ ok: false, error: { code, message, user_message? } }`.
  const errRaw = body.error;
  if (
    (body.ok === false || body.success === false) &&
    errRaw &&
    typeof errRaw === 'object'
  ) {
    const err = errRaw as Record<string, unknown>;
    const userMessage =
      pickString(err.user_message) ??
      pickString(err.message) ??
      pickString(body.message) ??
      NETWORK_FALLBACK_MESSAGE;
    const out: UnwrapResult<TData, TMeta> = {
      ok: false,
      code: pickString(err.code) ?? 'UNKNOWN_ERROR',
      userMessage,
    };
    if (err.field_errors && typeof err.field_errors === 'object') {
      out.fieldErrors = err.field_errors as Record<string, string[]>;
    }
    if (err.details && typeof err.details === 'object') {
      out.details = err.details as Record<string, unknown>;
    }
    const reqId = pickString(err.request_id) ?? pickString(body.request_id);
    if (reqId) out.requestId = reqId;
    return out;
  }

  // Unknown shape — last resort fallback.
  return {
    ok: false,
    code: 'UNKNOWN_SHAPE',
    userMessage: NETWORK_FALLBACK_MESSAGE,
  };
}

/**
 * Translate an `unwrapFinanceEnvelope` failure into a thrown `ApiError`
 * for callers that prefer the throw-style API. Success passes through.
 */
export function throwIfUnwrapFailed<TData, TMeta>(
  result: UnwrapResult<TData, TMeta>,
): asserts result is Extract<UnwrapResult<TData, TMeta>, { ok: true }> {
  if (result.ok) return;
  const env: ApiErrorEnvelope = {
    ok: false,
    error: {
      code: result.code,
      message: result.userMessage,
      ...(result.fieldErrors ? { field_errors: result.fieldErrors } : {}),
      ...(result.details ? { details: result.details } : {}),
      ...(result.requestId ? { request_id: result.requestId } : {}),
    },
  };
  throw new ApiError(0, env);
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

// ---------------------------------------------------------------------------
// Local axios clone — no response interceptor, so the body keeps both
// `data` (rows) AND `meta` (page/limit/total/summary). Cookie-credentialed
// so JWT pw_token + the CSRF double-submit cookie still flow on every
// read. CSRF header is not required on GETs (server only checks on
// mutating verbs), so the bare clone is sufficient for LIST endpoints.
// ---------------------------------------------------------------------------

const rawAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

// ---------------------------------------------------------------------------
// Query-string builder (drops undefined/null, supports array `type` for txns)
// ---------------------------------------------------------------------------

function buildQs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null) sp.append(k, String(item));
      }
      continue;
    }
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Raw GET — issues a read against `rawAxios` (no interceptor), then
// unwraps + validates the envelope locally with strict zod schemas so
// schema drift surfaces at the boundary, not deep inside a render.
// ---------------------------------------------------------------------------

// Accept either the canonical `{ ok: true }` shape or the legacy
// `{ success: true }` envelope. Some BE endpoints (notably
// `/api/v1/admin/credits` as of 2026-05-21) still ship the older form;
// requiring `ok` strictly here would surface a misleading
// "Phản hồi máy chủ sai định dạng" toast even on healthy 200 responses.
const RawSuccessEnvelopeSchema = z
  .object({
    ok: z.literal(true).optional(),
    success: z.literal(true).optional(),
    data: z.unknown(),
    meta: z.unknown().optional(),
    request_id: z.string().optional(),
  })
  .refine((v) => v.ok === true || v.success === true, {
    message: 'envelope missing ok / success flag',
  });

/**
 * Normalize the shape variations BE list endpoints emit into a single
 * `{ rows, meta }` pair before zod-validating each side.
 *
 * Canonical shape (most endpoints):
 *   { ok: true, data: [rows...], meta: { page, limit, total, summary? } }
 *
 * Nested-rows variant (e.g. /api/v1/admin/credits):
 *   { success: true, data: { rows: [...], page, pageSize, total, totalPages } }
 *
 * When the BE returns pagination flat inside `data`, we lift `rows` out and
 * synthesize a `meta` object (aliasing `pageSize` → `limit` so existing
 * MetaSchemas keep working).
 */
function splitRowsAndMeta(
  data: unknown,
  envMeta: unknown,
): { rows: unknown; meta: unknown } {
  if (Array.isArray(data)) {
    return { rows: data, meta: envMeta };
  }
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (Array.isArray(d.rows)) {
      const lifted: Record<string, unknown> = { ...(envMeta && typeof envMeta === 'object' ? envMeta as Record<string, unknown> : {}) };
      if (lifted.page === undefined && d.page !== undefined) lifted.page = d.page;
      if (lifted.limit === undefined) {
        if (d.limit !== undefined) lifted.limit = d.limit;
        else if (d.pageSize !== undefined) lifted.limit = d.pageSize;
      }
      if (lifted.total === undefined && d.total !== undefined) lifted.total = d.total;
      if (lifted.totalPages === undefined && d.totalPages !== undefined) lifted.totalPages = d.totalPages;
      return { rows: d.rows, meta: lifted };
    }
  }
  return { rows: data, meta: envMeta };
}

async function rawListGet<TRow, TMeta>(
  path: string,
  rowSchema: z.ZodType<TRow>,
  metaSchema: z.ZodType<TMeta>,
): Promise<{ rows: TRow[]; meta: TMeta }> {
  let body: unknown;
  try {
    const res = await rawAxios.get(path);
    body = res.data;
  } catch (err: unknown) {
    // Try to extract envelope from the AxiosError response body so
    // callers receive the API-provided user_message, NOT
    // AxiosError.message (banned by INTERFACE-STANDARDS).
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapFinanceEnvelope<unknown, unknown>(err.response.data);
      throwIfUnwrapFailed(unwrap);
      // If unwrap somehow succeeded on an error response, fall through.
    }
    // Network / abort / CORS — synthesise NETWORK_ERROR envelope.
    throw new ApiError(0, {
      ok: false,
      error: { code: 'NETWORK_ERROR', message: NETWORK_FALLBACK_MESSAGE },
    });
  }

  const unwrap = unwrapFinanceEnvelope<unknown, unknown>(body);
  throwIfUnwrapFailed(unwrap);

  // Validate envelope shape (accepts ok OR success per schema above).
  const envCheck = RawSuccessEnvelopeSchema.safeParse(body);
  if (!envCheck.success) {
    throw new ApiError(0, {
      ok: false,
      error: {
        code: 'INVALID_ENVELOPE',
        message: 'Server response had an invalid format',
      },
    });
  }

  // Normalize before zod-validating each half — handles both
  // `data: array + meta: {...}` and `data: { rows, page, pageSize, ... }`.
  const { rows: rowsRaw, meta: metaRaw } = splitRowsAndMeta(
    unwrap.data,
    envCheck.data.meta,
  );

  const rowsParse = z.array(rowSchema).safeParse(rowsRaw);
  const metaParse = metaSchema.safeParse(metaRaw);

  if (!rowsParse.success || !metaParse.success) {
    throw new ApiError(0, {
      ok: false,
      error: {
        code: 'INVALID_PAYLOAD',
        message: 'Response data did not match the schema',
        details: {
          row_errors: rowsParse.success ? undefined : rowsParse.error.issues,
          meta_errors: metaParse.success ? undefined : metaParse.error.issues,
        },
      },
    });
  }

  return { rows: rowsParse.data, meta: metaParse.data };
}

// ---------------------------------------------------------------------------
// LIST helpers
// ---------------------------------------------------------------------------

/** GET /api/v1/admin/finance/billing/topup-reviews */
export async function listTopupReviews(
  filters: TopupReviewListFilters = {},
): Promise<TopupReviewListPayload> {
  const path = `/api/v1/admin/finance/billing/topup-reviews${buildQs(
    filters as Record<string, unknown>,
  )}`;
  return rawListGet(path, TopupReviewRowSchema, TopupReviewMetaSchema);
}

/**
 * GET /api/v1/admin/credits — cross-merchant credit list.
 *
 * vg:debug F-015: BE adminCreditRoutesPlugin registered at /api/v1/admin
 * (not /api/v1/admin/finance). Plugin internal path "/credits" → real URL is
 * /api/v1/admin/credits. Drop erroneous "/finance" segment.
 */
export async function listFinanceCredits(
  filters: FinanceCreditListFilters = {},
): Promise<FinanceCreditListPayload> {
  const path = `/api/v1/admin/credits${buildQs(
    filters as Record<string, unknown>,
  )}`;
  return rawListGet(path, FinanceCreditRowSchema, FinanceCreditMetaSchema);
}

/** GET /api/v1/admin/payouts — vendor payout requests. */
export async function listPayouts(
  filters: PayoutListFilters = {},
): Promise<PayoutListPayload> {
  const path = `/api/v1/admin/payouts${buildQs(
    filters as Record<string, unknown>,
  )}`;
  return rawListGet(path, PayoutRowSchema, PayoutMetaSchema);
}

/** GET /api/v1/admin/transactions — cross-merchant/vendor ledger. */
export async function listTransactions(
  filters: TransactionListFilters = {},
): Promise<TransactionListPayload> {
  const path = `/api/v1/admin/transactions${buildQs(
    filters as Record<string, unknown>,
  )}`;
  return rawListGet(path, TransactionRowSchema, TransactionMetaSchema);
}

// ---------------------------------------------------------------------------
// Mutations — Topup approve / reject (Wave 2 Task 06)
// ---------------------------------------------------------------------------

/**
 * Reject reason enum mirroring API-CONTRACTS §POST /admin/topups/:id/reject
 * Block 2 (`TopupRejectReason`). Kept in lock-step with
 * `TopupRejectReason` in features/billing/TopupProofReviewModal.tsx —
 * any change must propagate to both sides.
 */
export const TopupRejectReasonSchema = z.enum([
  'wrong_amount',
  'wrong_memo',
  'unknown_sender',
  'duplicate',
  'suspicious',
  'missing_proof',
  'other',
]);
export type TopupRejectReason = z.infer<typeof TopupRejectReasonSchema>;

/** Request body for POST /admin/topups/:id/approve. */
// Phase 8.2 debug 2026-05-23 — `force_override + override_reason` mirrors
// the BE bypass for Tier-1 stuck-pending rows. BE requires `override_reason`
// to be ≥10 chars whenever `force_override` is true; the inline-approve CTA
// on /admin/billing/topup-reviews passes both so the row is recoverable
// even when the gateway webhook never lands.
export const ApproveTopupRequestSchema = z.object({
  notes: z.string().max(500).optional(),
  expected_version: z.number().int().min(0).optional(),
  force_override: z.boolean().optional(),
  override_reason: z.string().min(10).max(500).optional(),
});
export type ApproveTopupRequest = z.infer<typeof ApproveTopupRequestSchema>;

/**
 * Approve a topup (audit_log written backend-side).
 *
 * Shape MUST mirror the BE 200 body at
 * `apps/api/src/modules/payments/routes/topup.admin.ts` (the approve handler
 * returns `{ id, status, approved_at, approved_by_admin_id, usd_amount,
 * ledger_entry_id }`). dbg-20260609-multi: the prior interface declared
 * `audit_log_id`/`wallet_balance_cents`/`transaction_id` which the BE never
 * sends, so `res.audit_log_id.slice(-8)` in onSuccess threw on a 200 response.
 */
export interface ApproveTopupResponse {
  id: string;
  status: 'approved';
  approved_at: string;
  approved_by_admin_id: string;
  usd_amount: number;
  ledger_entry_id: string;
}

/**
 * POST /api/v1/admin/topups/:id/approve — credits the merchant wallet,
 * writes a ledger entry, and appends an audit_log row. Idempotency-Key
 * is generated client-side so refunds-on-retry stay safe.
 */
export async function approveTopup(
  topupId: string,
  body: ApproveTopupRequest,
): Promise<ApproveTopupResponse> {
  // Validate the body locally so a bad-callsite typo surfaces here
  // rather than hitting BE for a 400 round-trip.
  const parsed = ApproveTopupRequestSchema.parse(body);
  // dbg #2811 (UAT-FIN-014, 2026-07-25) — the BE approve schema
  // (`ApproveBodySchema` in topup.admin.ts) is `.strict()` and accepts
  // `internal_note` / `force_override` / `override_reason` only. The FE
  // "Approve notes (optional)" field maps to `internal_note`; sending the
  // old `notes` key (or `expected_version`) tripped `.strict()` → 400
  // "Unrecognized key(s) in object: 'notes'". Translate to the BE contract
  // here and drop any key the endpoint does not recognize.
  const bePayload: Record<string, unknown> = {};
  if (parsed.notes !== undefined) bePayload.internal_note = parsed.notes;
  if (parsed.force_override !== undefined)
    bePayload.force_override = parsed.force_override;
  if (parsed.override_reason !== undefined)
    bePayload.override_reason = parsed.override_reason;
  // Phase 8.2 debug 2026-05-23 — BE mounts the admin topup mutation
  // endpoints under `/admin/finance/billing/topup-reviews/*` (see app.ts
  // `topupAdminRoutes` registration). The legacy `/admin/topups/...` path
  // returns 404. Aligning here so both approve + reject hit the live BE.
  return apiClient.post<ApproveTopupResponse>(
    `/admin/finance/billing/topup-reviews/${encodeURIComponent(topupId)}/approve`,
    bePayload,
    { idempotencyKey: newIdempotencyKey() },
  );
}

/** Request body for POST /admin/topups/:id/reject. */
export const RejectTopupRequestSchema = z
  .object({
    reason: TopupRejectReasonSchema,
    reason_text: z.string().max(500).optional(),
    notify_merchant: z.boolean().default(true),
  })
  .refine(
    (v) =>
      v.reason !== 'other' ||
      (typeof v.reason_text === 'string' && v.reason_text.trim().length > 0),
    { message: 'reason_text required when reason=other', path: ['reason_text'] },
  );
export type RejectTopupRequest = z.infer<typeof RejectTopupRequestSchema>;

/**
 * Reject a topup (audit_log written backend-side).
 *
 * Shape MUST mirror the BE 200 body (the reject handler returns
 * `{ id, status, reason, rejected_at }`). dbg-20260609-multi: the prior
 * interface declared `notified`/`audit_log_id` which the BE never sends.
 */
export interface RejectTopupResponse {
  id: string;
  status: 'rejected';
  reason: string;
  rejected_at: string;
}

/**
 * POST /api/v1/admin/topups/:id/reject — does NOT refund automatically
 * (manual refund flow if money already credited). Notifies
 * merchant via email + in-app when `notify_merchant=true`.
 */
export async function rejectTopup(
  topupId: string,
  body: RejectTopupRequest,
): Promise<RejectTopupResponse> {
  const parsed = RejectTopupRequestSchema.parse(body);
  // Phase 8.2 debug 2026-05-23 — see approveTopup above for prefix
  // rationale. BE mounts reject at the same `/admin/finance/billing/
  // topup-reviews/:id/reject` path.
  return apiClient.post<RejectTopupResponse>(
    `/admin/finance/billing/topup-reviews/${encodeURIComponent(topupId)}/reject`,
    parsed,
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * RFC 4122 v4 idempotency key — defensive fallback when `crypto.randomUUID`
 * is unavailable (older test environments). Identical pattern to
 * apiClient.ts:newRequestId — kept local so finance.api stays usable
 * without re-exposing apiClient internals.
 */
function newIdempotencyKey(): string {
  const c =
    typeof crypto !== 'undefined' ? (crypto as Crypto & { randomUUID?: () => string }) : null;
  if (c?.randomUUID) return c.randomUUID();
  return (
    'idem_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

// ---------------------------------------------------------------------------
// Admin Invoices — list + bulk resend (Wave 3 Task 11)
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/admin/invoices — cross-merchant invoice list (
 *  A1). Status accepted as comma-joined list per zod `z.array`
 * input contract (each `?status=` repeats on the wire).
 *
 * @example
 *   const { rows, meta } = await listAdminInvoices({ status: ['OVERDUE'], limit: 20 });
 */
export async function listAdminInvoices(
  filters: AdminInvoiceListFilters = {},
): Promise<AdminInvoiceListPayload> {
  // status[] needs to be repeated as `?status=DRAFT&status=SENT` per the
  // zod `z.array(InvoiceStatus)` contract — buildQs already does this
  // for any Array value. overdue_only is z.coerce.boolean so 'true'
  // string is accepted; we send the boolean directly and let the QS
  // builder stringify it.
  const path = `/api/v1/admin/invoices${buildQs(
    filters as Record<string, unknown>,
  )}`;
  return rawListGet(path, AdminInvoiceRowSchema, AdminInvoiceListMetaSchema);
}

/**
 * POST /api/v1/admin/invoices/resend-email — single backend call that
 * fans out the email enqueue per `invoice_ids[]`. The server returns a
 * `queued_count` + `skipped[]` envelope so the UI can toast `N sent,
 * M skipped` without per-id round-trips.
 *
 * Idempotency-Key is required (422 IDEMPOTENCY_KEY_REQUIRED otherwise);
 * generated client-side per `newIdempotencyKey()`.
 *
 * Permission: `invoice:resend_email` (kế toán default).
 */
export async function bulkResendInvoiceEmail(
  body: BulkResendInvoiceEmailRequest,
): Promise<BulkResendInvoiceEmailResponse> {
  const parsed = BulkResendInvoiceEmailRequestSchema.parse(body);
  const raw = await apiClient.post<unknown>(
    '/admin/invoices/resend-email',
    parsed,
    { idempotencyKey: newIdempotencyKey() },
  );
  // `apiClient.post` already strips `{ok, data}` — parse the inner
  // payload defensively so a schema drift surfaces at the boundary.
  return BulkResendInvoiceEmailResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// Test seam — internals exported for unit tests only (do NOT use at runtime).
// ---------------------------------------------------------------------------

export const __test__ = {
  rawAxios,
  buildQs,
  RawSuccessEnvelopeSchema,
  newIdempotencyKey,
};
